'use client'

import * as Sentry from '@sentry/nextjs'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { SwiperSlide } from 'swiper/react'

import ZSwiper from '@/_components/universal/swiper/z-swiper'
import ZSuspenseError from '@/_components/universal/z-suspense-error'
import { cn } from '@/_lib/utils'

import { useGooglePlaceReviews } from '../_services/use-google-place-reviews.query'

import AHomeGoogleRatingStars from './_components/a-home-google-rating-stars'
import ZHomeSectionLayout, {
	ZHomeSectionLayoutDescription,
	ZHomeSectionLayoutTitle
} from './_components/z-home-section-layout'

export default function AHomeReviews({ className }: { className?: string }) {
	const locale = useLocale()
	const t = useTranslations('pages.home.google_reviews')
	const { data, isPending, isError, error } = useGooglePlaceReviews(locale)

	if (isPending) {
		return (
			<ZHomeSectionLayout
				id='google-recenzie'
				title={<ZHomeSectionLayoutTitle title={t('title')} />}
				description={<ZHomeSectionLayoutDescription description={t('description')} />}
				className={cn(className, 'bg-neutral-50')}
			>
				<div className='flex flex-col gap-10'>
					<div className='flex flex-col items-center gap-4 border border-neutral-200 bg-white/60 px-6 py-8'>
						<div className='h-14 w-24 animate-pulse rounded bg-neutral-200' />
						<div className='flex gap-1'>
							{Array.from({ length: 5 }).map((_, i) => (
								<div
									key={i}
									className='size-8 animate-pulse rounded bg-neutral-200'
								/>
							))}
						</div>
						<div className='h-4 w-40 animate-pulse rounded bg-neutral-200' />
					</div>
					<div className='min-h-[220px] animate-pulse rounded-lg border border-neutral-200 bg-neutral-100/80' />
				</div>
			</ZHomeSectionLayout>
		)
	}

	if (isError || data == null || data.rating == null) {
		if (error) {
			Sentry.captureException(error)
		}

		return <ZSuspenseError title='Nepodarilo sa načítať recenzie' />
	}

	const ratingLabel =
		data.userRatingsTotal != null
			? t('ratingCount').replace('{count}', String(data.userRatingsTotal))
			: ''

	const labels = {
		ratingCount: t('ratingCount'),
		noReviews: t('noReviews'),
		poweredByGoogle: t('poweredByGoogle')
	}

	const mapsPlaceId = data.mapsPlaceId ?? null
	const reviews = data.reviews

	return (
		<ZHomeSectionLayout
			title={<ZHomeSectionLayoutTitle title={t('title')} />}
			description={<ZHomeSectionLayoutDescription description={t('description')} />}
			className={cn(className, 'bg-neutral-50')}
			id='google-recenzie'
		>
			<div className='flex flex-col gap-10'>
				<div className='flex flex-col items-center gap-4 border border-neutral-200 bg-white/60 px-6 py-8 text-center md:flex-row md:justify-center md:gap-10 md:text-left'>
					<p className='sr-only'>
						{data.rating.toFixed(1)} / 5{ratingLabel ? ` ${ratingLabel}` : ''}
					</p>
					<div className='flex flex-col items-center gap-2 md:items-start'>
						<span className='text-5xl font-light tabular-nums md:text-6xl'>
							{data.rating.toFixed(1)}
						</span>
						<AHomeGoogleRatingStars rating={data.rating} size={32} />
						{ratingLabel ? (
							<span className='text-sm text-neutral-600'>{ratingLabel}</span>
						) : null}
					</div>
				</div>

				{reviews.length > 0 ? (
					<div className='w-full'>
						<ZSwiper
							spaceBetween={24}
							initialSlidesPerView={1}
							slidesPerGroup={1}
							loop={reviews.length > 1}
							autoplay={reviews.length > 1}
							autoplayDelay={6500}
							showArrows={reviews.length > 1}
							showProgressbar={reviews.length > 1}
							showCounter={reviews.length > 1}
							className='custom-swiper'
						>
							{reviews.map((review, index) => (
								<SwiperSlide
									key={`${review.authorName}-${review.dateFormatted}-${index}`}
								>
									<article className='flex min-h-[200px] flex-col gap-4 rounded-lg border border-neutral-200 bg-white/80 px-6 py-8 md:min-h-[220px]'>
										<div className='flex flex-wrap items-start justify-between gap-2'>
											<div>
												<p className='font-medium text-neutral-900'>
													{review.authorName}
												</p>
												<p className='text-sm text-neutral-500'>
													{review.dateFormatted}
												</p>
											</div>
											<AHomeGoogleRatingStars
												rating={review.rating}
												size={22}
											/>
										</div>
										<p className='text-base leading-relaxed text-neutral-800'>
											{review.text || '—'}
										</p>
									</article>
								</SwiperSlide>
							))}
						</ZSwiper>
					</div>
				) : (
					<p className='text-center text-neutral-600'>{labels.noReviews}</p>
				)}

				<p className='text-center text-xs text-neutral-500'>
					{mapsPlaceId ? (
						<Link
							className='underline underline-offset-2 hover:text-neutral-800'
							href={`https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(mapsPlaceId)}`}
							target='_blank'
							rel='noopener noreferrer'
						>
							{labels.poweredByGoogle}
						</Link>
					) : (
						labels.poweredByGoogle
					)}
				</p>
			</div>
		</ZHomeSectionLayout>
	)
}
